import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcIceJobV1JobQueryServiceJobGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcIceJobV1BatchToken, ExcIceJobV1JobResult, ExcIceJobV1JobSpec, ExcIceJobV1JobState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { TableSectionHeader } from '../../../../../../../../../../components/table/table-section-header';
import { IceJobEntityMenu } from '../../../../../../../../../../features/vendor/ice/job/ice-job-entity-menu';
import { IceJobEventSummary } from '../../../../../../../../../../features/vendor/ice/job/ice-job-event';
import {
  type getIceJobBatchTokenListItems,
  type getIceJobResultListItems,
  type getIceJobSpecListItems,
  getIceJobStateListItems,
} from '../../../../../../../../../../features/vendor/ice/job/ice-job-list-items';
import { getIceJobDetailPath } from '../../../../../../../../../../features/vendor/ice/job/ice-job-routing';
import { IceJobStatusPill } from '../../../../../../../../../../features/vendor/ice/job/ice-job-status-pill';
import { IceTasksTable } from '../../../../../../../../../../features/vendor/ice/task/ice-tasks-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/ice/job/_job/$jobId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ice-job-${params.jobId}`,
        label: 'Job',
        to: getIceJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('ICE Corporate Action Job'),
  component: IceJobDetail,
});

function getResultListItems(data: ExcIceJobV1JobResult | undefined, items: ReturnType<typeof getIceJobResultListItems>): ReadonlyListItem[] {
  if (!data) {
    return STABLE_EMPTY_ARRAY_REF;
  }

  if ('file' in data) {
    if ('file' in items) {
      return [items.file.value.bucket, items.file.value.key, items.file.value.size];
    }

    return STABLE_EMPTY_ARRAY_REF;
  }

  exhaustiveCheck(data);
  return STABLE_EMPTY_ARRAY_REF;
}

function getCursorListItems(data: ExcIceJobV1BatchToken | undefined, items: ReturnType<typeof getIceJobBatchTokenListItems>): ReadonlyListItem[] {
  if (!data) {
    return STABLE_EMPTY_ARRAY_REF;
  }

  if ('fileOffset' in data) {
    if ('fileOffset' in items) {
      return [items.fileOffset.value.offset];
    }

    return STABLE_EMPTY_ARRAY_REF;
  }

  if ('afterInstrument' in data) {
    if ('afterInstrument' in items) {
      return [items.afterInstrument.value.instrumentId];
    }

    return STABLE_EMPTY_ARRAY_REF;
  }

  exhaustiveCheck(data);
  return STABLE_EMPTY_ARRAY_REF;
}

function getSpecListItems(data: ExcIceJobV1JobSpec | undefined, items: ReturnType<typeof getIceJobSpecListItems>): ReadonlyListItem[] {
  if (!data) {
    return STABLE_EMPTY_ARRAY_REF;
  }

  if ('instrumentFile' in data) {
    if ('instrumentFile' in items) {
      return [
        items.instrumentFile.value.file.timestamp,
        items.instrumentFile.value.file.bucket,
        items.instrumentFile.value.file.key,
        items.instrumentFile.value.file.size,
      ];
    }

    return STABLE_EMPTY_ARRAY_REF;
  }

  if ('corpActionFile' in data) {
    if ('corpActionFile' in items) {
      return [
        items.corpActionFile.value.file.timestamp,
        items.corpActionFile.value.file.bucket,
        items.corpActionFile.value.file.key,
        items.corpActionFile.value.file.size,
      ];
    }

    return STABLE_EMPTY_ARRAY_REF;
  }

  if ('reparseInstruments' in data) {
    if ('reparseInstruments' in items) {
      return [items.reparseInstruments.value.reason];
    }

    return STABLE_EMPTY_ARRAY_REF;
  }

  if ('exportInstruments' in data) {
    if ('exportInstruments' in items) {
      return [items.exportInstruments.value.date];
    }

    return STABLE_EMPTY_ARRAY_REF;
  }

  exhaustiveCheck(data);
  return STABLE_EMPTY_ARRAY_REF;
}

function getListItems(data: ExcIceJobV1JobState | undefined) {
  const items = getIceJobStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [
          ...(items.data.failure ? [items.data.failure] : []),
          items.data.spec.typeName,
          ...getSpecListItems(data?.data?.spec, items.data.spec),
        ],
      },
      ...(items.data.result
        ? [
            {
              id: 'result',
              title: 'Result',
              items: [items.data.result.typeName, ...getResultListItems(data?.data?.result, items.data.result)],
            },
          ]
        : []),
      ...(items.data.cursor
        ? [
            {
              id: 'cursor',
              title: 'Cursor',
              items: [items.data.cursor.typeName, ...getCursorListItems(data?.data?.cursor, items.data.cursor)],
            },
          ]
        : []),
      ...(items.data.metrics
        ? [
            {
              id: 'metrics',
              title: 'Metrics',
              items: [
                items.data.metrics.total,
                items.data.metrics.succeeded,
                items.data.metrics.pending,
                items.data.metrics.errored,
                items.data.metrics.failed,
              ],
            },
          ]
        : []),
    ] as const satisfies ReadonlyListGroup[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.jobId] as const satisfies ReadonlyListItem[],
  } as const;
}

function TasksTableHeader(props: CustomTableHeaderProps) {
  return <TableSectionHeader {...props} title="Tasks" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load ICE job', 'view this ICE job');

function IceJobDetail() {
  const { jobId } = Route.useParams();
  const request = { jobId } as const;
  const { data, error, isPending } = useExcIceJobV1JobQueryServiceJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.job), [data?.job]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <IceJobStatusPill isPending={showPending} size="large" status={data?.job?.status} />
          </HorizontalStack>

          <IceJobEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <IceJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <IceTasksTable HeaderActions={TasksTableHeader} jobId={jobId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
